import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Leftsidebar from "../sidebar/leftsidebar";
import LeftResponsivesidebar from "../sidebar/left-responsive";
import Headmenu from "../sidebar/headmenu";
import Loading from "../common/Loading";
import { getAd, updateAd, submitAd, getAdCtas, uploadAdMedia, getMySellerId, getMyProducts } from "../../hook/useAdsApi";
import {
  AD_STATUS,
  AD_DESTINATION_TYPES,
  OBJECTIVE_DESTINATION_MAP,
  CTA_ICONS,
  getCurrentUserId,
} from "./adsConstants";

export default function EditAd() {
  const { adId } = useParams();
  const navigate = useNavigate();
  const [ad, setAd] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ctas, setCtas] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [mediaUrlInput, setMediaUrlInput] = useState("");
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [thumbnailUrlInput, setThumbnailUrlInput] = useState("");
  const [myProducts, setMyProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState(null);

  useEffect(() => {
    getAd(adId)
      .then((result) => {
        setAd(result);
        setForm({
          name: result.name || "",
          headline: result.headline || "",
          description: result.body_text || "",
          cta: result.cta || "",
          creativeType: result.creative_type || "image",
          destinationType: result.destination_type || "",
          destinationId: result.destination_id || "",
          mediaFiles: result.creative_media || [],
          thumbnail: result.thumbnail || null,
        });
      })
      .catch(() => toast.error("Failed to load ad", { toastId: "edit-ad-load-error" }))
      .finally(() => setLoading(false));
  }, [adId]);

  useEffect(() => {
    if (!ad?.campaign?.objective) return;
    getAdCtas(ad.campaign.objective)
      .then((result) => setCtas(result?.ctas || []))
      .catch(() => setCtas([]));
  }, [ad]);

  const updateForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const allowedDestinations = ad ? OBJECTIVE_DESTINATION_MAP[ad.campaign?.objective] || [] : [];
  const destinationOptions = AD_DESTINATION_TYPES.filter((dest) => allowedDestinations.includes(dest.value));
  const isProductDestination = form?.destinationType === "product" || form?.destinationType === "marketplace_listing";

  // Product/Marketplace Listing destination both resolve to the same Product table on the
  // backend - /buyer/get-products has no auto-scoping, so this resolves the caller's own
  // seller_id first, then filters by it - fetched once, on first switching to either.
  useEffect(() => {
    if (!isProductDestination || myProducts.length > 0 || productsLoading) return;
    setProductsLoading(true);
    getMySellerId()
      .then((sellerId) => (sellerId ? getMyProducts(sellerId) : []))
      .then(setMyProducts)
      .catch(() => setProductsError("Couldn't load your products. Try again later."))
      .finally(() => setProductsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProductDestination]);

  const selectDestinationType = (value) => {
    // "profile" targets the logged-in user's own id automatically; product/live/marketplace
    // listing need a real destination_id - client-api only checks that the id exists.
    updateForm({ destinationType: value, destinationId: value === "profile" ? getCurrentUserId() : "" });
  };

  const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp"];
  const VIDEO_EXTENSIONS = ["mp4", "mov", "webm", "ogg"];

  // Same one-item cap as CreateAd.js - client-api's creative_media schema is min(1).max(1).
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 1);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = await uploadAdMedia(files);
      updateForm({ mediaFiles: uploaded });
    } catch {
      toast.error("Failed to upload media");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  // Testing-only fallback: same as CreateAd.js - the /aws/generate-presigned-url upload
  // currently fails (backend AWS credentials issue), and this checks the pasted URL's
  // extension matches the Image/Video toggle so it doesn't silently save as the wrong type.
  const addMediaByUrl = () => {
    const url = mediaUrlInput.trim();
    if (!url) return;

    const extensionMatch = url.split(/[?#]/)[0].match(/\.([a-zA-Z0-9]+)$/);
    const extension = extensionMatch?.[1]?.toLowerCase();
    const wrongTypeExtensions = form.creativeType === "video" ? IMAGE_EXTENSIONS : VIDEO_EXTENSIONS;

    if (extension && wrongTypeExtensions.includes(extension)) {
      toast.error(
        form.creativeType === "video"
          ? "That looks like an image URL, not a video - it'll show broken if added."
          : "That looks like a video URL, not an image - it'll show broken if added."
      );
      return;
    }

    updateForm({ mediaFiles: [{ url, media_type: form.creativeType }] });
    setMediaUrlInput("");
  };

  const removeMedia = () => updateForm({ mediaFiles: [] });

  // client-api requires a separate poster-image thumbnail for every video ad (the video file
  // can't serve as its own thumbnail - thumbnailSchema only accepts media_type:'image').
  const handleThumbnailFileChange = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 1);
    if (files.length === 0) return;
    setThumbnailUploading(true);
    try {
      const uploaded = await uploadAdMedia(files);
      updateForm({ thumbnail: uploaded[0] });
    } catch {
      toast.error("Failed to upload thumbnail");
    } finally {
      setThumbnailUploading(false);
      e.target.value = "";
    }
  };

  const addThumbnailByUrl = () => {
    const url = thumbnailUrlInput.trim();
    if (!url) return;

    const extensionMatch = url.split(/[?#]/)[0].match(/\.([a-zA-Z0-9]+)$/);
    const extension = extensionMatch?.[1]?.toLowerCase();
    if (extension && VIDEO_EXTENSIONS.includes(extension)) {
      toast.error("That looks like a video URL, not an image - it'll show broken if added.");
      return;
    }

    updateForm({ thumbnail: { url, media_type: "image" } });
    setThumbnailUrlInput("");
  };

  const removeThumbnail = () => updateForm({ thumbnail: null });

  // client-api rejects edits to an ad that's Submitted for Review - only Draft, Rejected,
  // Approved, Active, and Paused are editable.
  const isEditable = ad && ad.status !== AD_STATUS.SUBMITTED;

  const canSave =
    form &&
    form.headline.trim() &&
    form.description.trim() &&
    form.cta &&
    form.destinationType &&
    form.destinationId &&
    form.mediaFiles.length > 0 &&
    (form.creativeType !== "video" || form.thumbnail);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAd(adId, {
        name: form.name,
        headline: form.headline,
        body_text: form.description,
        cta: form.cta,
        creative_type: form.creativeType,
        destination_type: form.destinationType,
        destination_id: form.destinationId,
        creative_media: form.mediaFiles,
        // thumbnailSchema requires media_type:'image' strictly, and client-api separately
        // requires a thumbnail outright for video creatives - video sends the dedicated
        // poster image collected below, image sends the media itself.
        thumbnail: form.creativeType === "video" ? form.thumbnail : form.mediaFiles[0],
      });

      // updateAd only auto-reverts Approved/Active/Paused ads to "Submitted for Review"
      // when a review-relevant field changed. Draft/Rejected ads stay as-is after an
      // update, so submit explicitly to actually resubmit them.
      if (ad.status === AD_STATUS.DRAFT || ad.status === AD_STATUS.REJECTED) {
        await submitAd(adId);
      }

      toast.success("Ad saved");
      navigate(`/ads/ads/${adId}`);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update ad");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />
      <main className="flex-1 p-6 pl-8">
        <LeftResponsivesidebar />
        <Headmenu />
        <main className="flex">
          <div className="w-full max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <button onClick={() => navigate(-1)} className="rounded-full bg-[#e7f3ea] p-2">
                ←
              </button>
              <h1 className="text-xl font-bold">Edit Ad</h1>
            </div>

            {loading || !ad ? (
              <div className="flex justify-center mt-16">
                <Loading />
              </div>
            ) : !isEditable ? (
              <p className="text-gray-500 text-sm">
                This ad is currently under review and can't be edited until a decision is made.
              </p>
            ) : (
              <>
                <label className="block text-sm font-semibold mb-1">Ad Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => updateForm({ name: e.target.value })}
                  className="w-full border rounded-xl px-4 py-3 mb-6"
                />

                <label className="block text-sm font-semibold mb-2">Images/videos.</label>
                <div className="flex gap-3 mb-4">
                  {["image", "video"].map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => updateForm({ creativeType: t })}
                      className={`px-5 py-2 rounded-full text-sm font-semibold capitalize ${
                        form.creativeType === t
                          ? "bg-[#095228] text-white"
                          : "border border-[#c3c5d9] text-[#434656]"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>

                <label className="block text-sm font-semibold mb-2">
                  {form.creativeType === "video" ? "Upload video" : "Upload images"}
                </label>
                <label className="flex items-center justify-between border rounded-xl px-4 py-3 mb-2 cursor-pointer text-gray-400">
                  <span>
                    {uploading
                      ? "Uploading..."
                      : form.creativeType === "video"
                      ? "Upload video."
                      : "Upload images."}
                  </span>
                  <input
                    type="file"
                    accept={form.creativeType === "image" ? "image/*" : "video/*"}
                    onChange={handleFileChange}
                    disabled={uploading}
                    className="hidden"
                  />
                  <span>⬆</span>
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={mediaUrlInput}
                    onChange={(e) => setMediaUrlInput(e.target.value)}
                    placeholder={
                      form.creativeType === "video"
                        ? "Or paste a video URL (testing only - upload is currently broken)"
                        : "Or paste an image URL (testing only - upload is currently broken)"
                    }
                    className="flex-1 border rounded-xl px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    onClick={addMediaByUrl}
                    className="px-4 py-2 rounded-xl border border-[#095228] text-[#095228] text-sm font-semibold"
                  >
                    Add
                  </button>
                </div>
                {form.mediaFiles.length > 0 && (
                  <div className="flex gap-3 mb-6">
                    {form.mediaFiles.map((media) => (
                      <div key={media.url} className="relative">
                        {media.media_type === "video" ? (
                          <video src={media.url} controls className="w-20 h-20 object-cover rounded-xl" />
                        ) : (
                          <img src={media.url} alt="" className="w-20 h-20 object-cover rounded-xl" />
                        )}
                        <button
                          type="button"
                          onClick={removeMedia}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {form.creativeType === "video" && (
                  <>
                    <label className="block text-sm font-semibold mb-2">Thumbnail Image</label>
                    <label className="flex items-center justify-between border rounded-xl px-4 py-3 mb-2 cursor-pointer text-gray-400">
                      <span>{thumbnailUploading ? "Uploading..." : "Upload a thumbnail image."}</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleThumbnailFileChange}
                        disabled={thumbnailUploading}
                        className="hidden"
                      />
                      <span>⬆</span>
                    </label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={thumbnailUrlInput}
                        onChange={(e) => setThumbnailUrlInput(e.target.value)}
                        placeholder="Or paste a thumbnail image URL (testing only - upload is currently broken)"
                        className="flex-1 border rounded-xl px-3 py-2 text-sm"
                      />
                      <button
                        type="button"
                        onClick={addThumbnailByUrl}
                        className="px-4 py-2 rounded-xl border border-[#095228] text-[#095228] text-sm font-semibold"
                      >
                        Add
                      </button>
                    </div>
                    {form.thumbnail && (
                      <div className="flex gap-3 mb-2">
                        <div className="relative">
                          <img src={form.thumbnail.url} alt="" className="w-20 h-20 object-cover rounded-xl" />
                          <button
                            type="button"
                            onClick={removeThumbnail}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mb-6">Required for video ads - JPG or PNG poster image.</p>
                  </>
                )}

                <label className="block text-sm font-semibold mb-1">Ad Headline</label>
                <input
                  type="text"
                  value={form.headline}
                  onChange={(e) => updateForm({ headline: e.target.value })}
                  className="w-full border rounded-xl px-4 py-3 mb-6"
                />

                <label className="block text-sm font-semibold mb-1">Ad Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => updateForm({ description: e.target.value })}
                  rows={5}
                  className="w-full border rounded-xl px-4 py-3 mb-6"
                />

                <label className="block text-sm font-semibold mb-2">Call to Action</label>
                <div className="flex flex-wrap gap-2 mb-6">
                  {ctas.map((cta) => {
                    const selected = form.cta === cta.code;
                    const icon = CTA_ICONS[cta.code];
                    return (
                      <button
                        key={cta.code}
                        type="button"
                        onClick={() => updateForm({ cta: cta.code })}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${
                          selected ? "bg-[#095228] text-white" : "border border-[#c3c5d9] text-[#434656]"
                        }`}
                      >
                        {selected && icon && <img src={icon} alt="" className="w-4 h-4" />}
                        {cta.label}
                      </button>
                    );
                  })}
                </div>

                <label className="block text-sm font-semibold mb-2">Destination Type</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {destinationOptions.map((dest) => (
                    <button
                      key={dest.value}
                      type="button"
                      onClick={() => selectDestinationType(dest.value)}
                      className={`px-4 py-2 rounded-full text-sm font-semibold ${
                        form.destinationType === dest.value
                          ? "bg-[#095228] text-white"
                          : "border border-[#c3c5d9] text-[#434656]"
                      }`}
                    >
                      {dest.label}
                    </button>
                  ))}
                </div>
                {isProductDestination && (
                  <>
                    {productsError && <p className="text-red-500 text-xs mb-2">{productsError}</p>}
                    <select
                      value={form.destinationId}
                      onChange={(e) => updateForm({ destinationId: e.target.value })}
                      className="w-full border border-[#c3c5d9] rounded-xl px-4 py-3 mb-2 text-sm"
                    >
                      <option value="">{productsLoading ? "Loading your products..." : "Select a product"}</option>
                      {myProducts.map((p) => (
                        <option key={p.id} value={p.id}>{p.product_name}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mb-10">
                      {!productsLoading && !productsError && myProducts.length === 0
                        ? "You don't have any product listings yet."
                        : "Choose one of your own product listings."}
                    </p>
                  </>
                )}
                {form.destinationType === "live" && (
                  <>
                    <input
                      type="number"
                      value={form.destinationId}
                      onChange={(e) => updateForm({ destinationId: e.target.value })}
                      placeholder="Enter Live Stream ID"
                      className="w-full border border-[#c3c5d9] rounded-xl px-4 py-3 mb-2"
                    />
                    <p className="text-xs text-gray-500 mb-10">The numeric ID of one of your own live streams.</p>
                  </>
                )}
                {(!form.destinationType || form.destinationType === "profile") && <div className="mb-8" />}

                <button
                  type="button"
                  disabled={!canSave || saving}
                  onClick={handleSave}
                  className="w-full bg-[#095228] text-white font-semibold py-3 rounded-full disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save & Resubmit"}
                </button>
              </>
            )}
          </div>
        </main>
      </main>
    </div>
  );
}
